import type { IndustryPage } from "@/lib/data";

export const architects: IndustryPage = {
  slug: "architects",
  seoTitle:
    "Web Design for Architects UK | Portfolio-Led Websites",
  seoDescription:
    "Web design for architects and practices: portfolio layouts, project case studies, team pages and local SEO. Fixed £1,000, live in 7 days. Book a call.",
  serviceName: "Website design for architects",
  tldr:
    "ConstructionWebDesign.uk offers web design for architects and practices across the UK: portfolio-led websites with a case study for each project, team pages and the RIBA or ARB details you hold. Each site costs £1,000, a one-off fixed price with no subscription, or £1,399 with logo and branding. Your website is live in 7 days, and you own it.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Architect website design · UK",
    headline: "Website design for architects that leads with the portfolio.",
    highlight: "leads with the portfolio",
    sub: "Websites for UK architects and practices with a portfolio-led homepage, a case study page for each project, pages for each service and planning stage, team pages, a first-meeting enquiry form, the RIBA, ARB or CIAT details you hold and on-page local SEO.",
    secondaryCta: { label: "See architect demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/industry-hero/Architects.jpg",
      alt: "Completed contemporary residential project with clean lines and considered materials, the kind of work an architect website should show",
      headlineTop: "Win the clients you",
      headlineBottom: "actually want to work for.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/avelon.jpg",
    },
  ],
  demoCategory: "Construction & Architecture",
  demosHeading: "Architect website demo templates.",
  demosSub:
    "Demo templates for architecture practices and design-build firms. Open one to see the layout, portfolio pages and features an architect website can include. These are demos, not client sites.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does an architect need a website?",
    intro:
      "An architect needs a website because clients judge a practice by its past projects before they ask for a first meeting. An architect website shows your portfolio, the services you offer, the people in the practice and your registrations in one place, and takes enquiries while you're busy on projects.",
    points: [
      {
        title: "Let the work set the tone.",
        body: "A client can't tell from a list of services whether your design approach suits their project. A calm, image-led site with properly photographed projects shows your style and the standard of your work before the first conversation.",
        icon: "Compass",
      },
      {
        title: "Give every project its own case study.",
        body: "Each completed project can have its own case study page with photography, drawings, the brief and the outcome. A case study named by project type and location, such as a rear extension in a conservation area, gives Google something specific to match when a client searches for that kind of work.",
        icon: "Sparkle",
      },
      {
        title: "Get found for \"architect near me\".",
        body: "On-page local SEO, pages for the areas and project types you cover and a link to your Google Business Profile help Google match your website to architecture searches near your studio.",
        icon: "Pin",
      },
      {
        title: "Show the registrations clients check.",
        body: "ARB registration, RIBA Chartered Practice status, CIAT membership and conservation accreditation sit where visitors look first, with links to the official registers. Only the registrations and memberships you actually hold go on the site.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What does good web design for architects include?",
    intro:
      "A good architect website puts the portfolio first, then gives each project, each service and each person in the practice a clear page, with a simple way to start a conversation. Every architect website from ConstructionWebDesign.uk is planned around the parts below.",
    items: [
      {
        title: "A portfolio-led homepage.",
        body: "The homepage opens with your projects, not a list of services. Photography runs full width, with generous spacing and clean type, so the work sets the tone and the layout stays out of the way. The design is mobile-first, so the portfolio still reads well on a phone.",
      },
      {
        title: "A case study page for each project.",
        body: "Each project gets its own page: photography, drawings, the brief, the site constraints, the planning route and the finished result. Case studies can be grouped by type, such as residential, extensions, listed buildings, conservation areas, retrofit, commercial or Passivhaus, so a client can find work like theirs.",
      },
      {
        title: "Service and planning pages.",
        body: "Pages for each service, from feasibility and concept design to planning applications, listed building consent, building regulations drawings and contract administration. Each page explains what happens at that stage, using the RIBA Plan of Work stages if that's how your practice works.",
      },
      {
        title: "RIBA, ARB and CIAT details near the top.",
        body: "In the UK, only people on the register kept by ARB (the Architects Registration Board) can use the title architect. If you're ARB registered, a RIBA Chartered Practice, a CIAT member or on a conservation register such as AABC, the details sit in the header with links to the official register. Anything you don't hold stays off the site.",
      },
      {
        title: "Team pages for the people clients will meet.",
        body: "A team page introduces the directors, architects and technologists with a photo, their role and their registrations. A client choosing an architect is choosing who they'll work with for months, so the team page shows them who that is.",
      },
      {
        title: "A first-meeting enquiry form.",
        body: "The enquiry form asks for the project type, the site location, a budget band and the planning stage, so you can see whether the project fits before you reply. A client gets a clear next step instead of a generic contact box.",
      },
      {
        title: "Local SEO built in from day one.",
        body: "Every architect website includes on-page local SEO: schema markup, page titles with your town and project types, case study pages named by location and a link to your Google Business Profile.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does an architect website cost in the UK?",
      a: "An architect website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription, or £1,399 with a logo and branding. For comparison, typical UK ranges are about £4,000–£5,000 for a freelancer billing £50–£120 an hour, £5,000–£25,000 for an agency, or about £22 a month for Wix or Squarespace. The practice owns the website.",
    },
    {
      q: "How long does it take to build an architect website?",
      a: "An architect website from ConstructionWebDesign.uk goes live 7 days after the discovery call. The five steps are a discovery call, strategy and sitemap, design, build, then launch and support. On the discovery call you choose the projects to feature, the services to list and the people for the team page. For comparison, a typical UK agency project takes 8–12 weeks.",
    },
    {
      q: "What pages should an architect website have?",
      a: "An architect website should have a portfolio-led homepage, a case study page for each featured project, a page for each service (feasibility, planning, building regulations, contract administration), a team page, an about page for the practice and a contact page with a first-meeting enquiry form. RIBA, ARB or CIAT details belong near the top if you hold them. ConstructionWebDesign.uk agrees the sitemap with you before design starts.",
    },
    {
      q: "Do architects need a website?",
      a: "Yes, architects need a website, because a client choosing an architect wants to see past projects before asking for a first meeting. An architect website shows your portfolio, services, team and registrations in one place. Instagram, Houzz and directory profiles help, but they sit on someone else's platform. Your own website shows only your practice, takes enquiries while you're busy on projects and belongs to you.",
    },
    {
      q: "How do architects get found on Google?",
      a: "Architects get found on Google through a website with pages for each service, project type and area, on-page local SEO and a complete Google Business Profile with regular reviews. Web design for architects from ConstructionWebDesign.uk includes on-page local SEO: schema markup, case study pages named by project type and location, town keywords in page titles and a link to your Google Business Profile. Nobody can honestly guarantee a ranking, but these are the basics a practice needs.",
    },
    {
      q: "Should an architect show their ARB registration on their website?",
      a: "Yes, an architect should show their ARB registration on the website, because in the UK only people on the ARB register can use the title architect. Clients can check a name on the Architects Registration Board's online register. Architect website design from ConstructionWebDesign.uk puts ARB, RIBA Chartered Practice and CIAT details near the top of the page, linked to the official register, and leaves off anything the practice doesn't hold.",
    },
    {
      q: "What makes good architecture website design?",
      a: "Good architecture website design lets the projects lead: full-width photography, clean type, generous spacing and a case study page for each project. Navigation stays simple, with portfolio, services, team and contact. ConstructionWebDesign.uk builds architect websites this way, with project types such as residential, extensions, listed buildings and commercial grouped so a client can quickly find work similar to theirs.",
    },
    {
      q: "Can a sole practitioner architect get a website?",
      a: "Yes, a sole practitioner architect can get a website from ConstructionWebDesign.uk at the same fixed £1,000 price as a larger practice. The sitemap is sized to the practice: a sole practitioner might feature a handful of projects and a single profile, while a studio adds a full team page and more case studies. Website design for architects is planned around the work you want more of.",
    },
  ],
  guides: [
    {
      href: "/blog/how-much-should-a-construction-website-cost",
      label: "How much does a construction website cost in the UK?",
    },
    {
      href: "/blog/online-presence-construction-company",
      label: "Online presence for construction companies: a guide",
    },
    {
      href: "/blog/wordpress-vs-custom-construction-website",
      label: "WordPress vs custom website for construction firms",
    },
    {
      href: "/blog/google-business-profile-for-builders",
      label: "Google Business Profile for builders: setup guide",
    },
  ],
  finalCta: {
    headline: "Talk to us about your architect website.",
    highlight: "architect website",
    sub: "Book a free 30-minute call or email hello@constructionwebdesign.uk. Fixed £1,000, one-off, and your website is live in 7 days.",
  },
};
